import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n";
import api from "../api";
import useSeoHead from "../useSeoHead";
import VerifiedBadge from "../components/VerifiedBadge";
import {
  ArrowRight, Inbox, Globe2, ShieldCheck, Truck, Factory, Lock,
  Quote, MapPin, Clock, TrendingUp, Eye, Zap, Flag, Star, MessageCircle,
  Award, Sparkles,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1717386255773-1e3037c81788?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxtYW51ZmFjdHVyaW5nJTIwZmFjdG9yeXxlbnwwfHx8fDE3Nzg0NTk5NTF8MA&ixlib=rb-4.1.0&q=85";
const LOGISTICS_IMG = "https://images.pexels.com/photos/11087837/pexels-photo-11087837.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const WA_LIST_URL = "https://wa.me/19154992230?text=Hola%2C%20quiero%20listar%20mi%20planta%20en%20Corridor%20Digital";

export default function Landing() {
  const { t, lang } = useI18n();
  const [stats, setStats] = useState(null);
  const [toolkitStats, setToolkitStats] = useState(null);
  const [catCount, setCatCount] = useState(null);

  useEffect(() => { api.get("/public/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/sales-toolkit/stats").then(({ data }) => setToolkitStats(data)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/public/capabilities").then(({ data }) => {
      const list = Array.isArray(data) ? data : data?.capabilities || [];
      if (list.length) setCatCount(list.length);
    }).catch(() => {});
  }, []);

  /* Brief SEO V2 — Homepage SEO head */
  useSeoHead({
    title:
      lang === "es"
        ? "Directorio IMMEX Verificado · Chihuahua–El Paso | Corridor Digital"
        : "Verified IMMEX Directory · Chihuahua–El Paso | Corridor Digital",
    description:
      lang === "es"
        ? "El directorio bilingüe de registro de fabricantes IMMEX en el corredor Chihuahua–El Paso (Juárez, Parral, Chihuahua City). 644 ubicaciones de planta listadas por gobierno, 81 categorías de capacidad, RFQ directo."
        : "The bilingual directory of record for IMMEX manufacturers in the Chihuahua–El Paso corridor (Juárez, Parral, Chihuahua City). 644 government-listed plant locations, 81 capability categories, direct RFQs.",
    path: "/",
    lang,
    jsonLd: [
      {
        id: "ld-website",
        payload: {
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Corridor Digital",
          url: "https://corridordigital.net/",
          inLanguage: ["es-MX", "en-US"],
          potentialAction: {
            "@type": "SearchAction",
            target: {
              "@type": "EntryPoint",
              urlTemplate:
                "https://corridordigital.net/directory?q={search_term_string}",
            },
            "query-input": "required name=search_term_string",
          },
        },
      },
    ],
  });

  const spotsLeft = stats?.founding_spots_remaining ?? 10;
  const spotsTaken = Math.max(0, (stats?.founding_spots_total ?? 10) - spotsLeft);
  const usBuyersThisMonth = stats?.us_buyers_this_month ?? 47;

  const C = {
    en: {
      founding: `${spotsLeft} of 10 founding spots available — lock $149/mo for life`,
      foundingCta: "See founding offer",
      lossKicker: "WHAT YOU'RE LOSING WHILE THEY CAN'T FIND YOU",
      lossTitle: "$150M in cross-border procurement flows through this corridor each year.\nIf US buyers can't find you online, that revenue routes elsewhere.",
      lossStats: [
        ["73%", "of IMMEX plants in Juárez have zero English presence"],
        ["0", "competing platforms exist in the corridor today"],
        ["18mo", "before national agencies notice this opportunity"],
        ["$2.3B", "in daily US-Mexico trade routes through Juárez–El Paso"],
      ],
      socialKicker: "TRUSTED BY MANUFACTURERS BUILDING THE CORRIDOR",
      caseKicker: "CASE STUDY · APEX DIGITAL",
      caseTitle: "How Industrias Frontera MX closed a $2.4M contract in 6 weeks.",
      caseStats: [
        ["$2.4M", "Initial contract"],
        ["6 wks", "Bilingual platform live"],
        ["12", "Page-1 keywords, EN + ES"],
        ["4", "US RFQs in 30 days"],
      ],
      caseQuote: "They told us nobody targets maquiladoras. We told them — exactly.",
      caseAttrib: "— Derick Varela, Founder · Apex Digital · Las Cruces, NM",
      bridgeKicker: "THE PIECE NO ONE ELSE OFFERS",
      bridgeTitle: "Cross-Border Data Bridge.",
      bridgeBody: "Your top US client logs into a private dashboard and watches your compliance score in real time. No more email chains, no more audits over WhatsApp.",
      bridgeBullets: [
        "Invite-only access for your US partners — no public exposure",
        "Live compliance score updated as you upload docs",
        "View tracking — see when your buyer opens what",
        "Premium feature. Enterprise: customized per client.",
      ],
      bridgeCta: "See how it works",
      finalKicker: "CORRIDOR DIGITAL · 2026 EDITION",
      finalTitle: "Ship to El Paso.\nGet found from Detroit.",
      finalCta: "List your factory",
      finalCta2: "Browse the directory",
      guarantee: "60-DAY MONEY-BACK GUARANTEE · NO QUALIFIED US INQUIRY = FULL REFUND",
      live: "LIVE ON THE CORRIDOR",
    },
    es: {
      founding: `${spotsLeft} de 10 lugares disponibles — fija $149/mes de por vida`,
      foundingCta: "Ver oferta fundador",
      lossKicker: "LO QUE ESTÁS PERDIENDO MIENTRAS NO TE ENCUENTRAN",
      lossTitle: "$150M en compras transfronterizas pasan por este corredor cada año.\nSi los compradores en EE.UU. no te encuentran en línea, ese dinero va a otro lado.",
      lossStats: [
        ["73%", "de plantas IMMEX en Juárez sin presencia en inglés"],
        ["0", "plataformas competidoras existen hoy en el corredor"],
        ["18mo", "antes que agencias nacionales noten esto"],
        ["$2.3B", "en comercio diario EE.UU.–México por Juárez–El Paso"],
      ],
      socialKicker: "FABRICANTES CONSTRUYENDO EL CORREDOR",
      caseKicker: "CASO · APEX DIGITAL",
      caseTitle: "Cómo Industrias Frontera MX cerró un contrato de $2.4M en 6 semanas.",
      caseStats: [
        ["$2.4M", "Contrato inicial"],
        ["6 sem", "Plataforma bilingüe en vivo"],
        ["12", "Keywords página 1, EN + ES"],
        ["4", "Cotizaciones EE.UU. en 30 días"],
      ],
      caseQuote: "Nos dijeron que nadie le apunta a las maquiladoras. Nosotros dijimos — exacto.",
      caseAttrib: "— Derick Varela, Fundador · Apex Digital · Las Cruces, NM",
      bridgeKicker: "LA PIEZA QUE NADIE MÁS OFRECE",
      bridgeTitle: "Cross-Border Data Bridge.",
      bridgeBody: "Tu cliente principal en EE.UU. entra a un dashboard privado y ve tu puntaje de cumplimiento en tiempo real. Ya no más cadenas de correos ni auditorías por WhatsApp.",
      bridgeBullets: [
        "Acceso por invitación a tus partners EE.UU. — sin exposición pública",
        "Puntaje de cumplimiento en vivo conforme subes documentos",
        "Rastreo de vistas — ve cuándo abre qué tu comprador",
        "Funcionalidad Premium. Enterprise: personalizada por cliente.",
      ],
      bridgeCta: "Ver cómo funciona",
      finalKicker: "CORRIDOR DIGITAL · EDICIÓN 2026",
      finalTitle: "Embarca a El Paso.\nSé encontrado desde Detroit.",
      finalCta: "Lista tu planta",
      finalCta2: "Explorar el directorio",
      guarantee: "GARANTÍA DE 60 DÍAS · SIN COTIZACIÓN CALIFICADA = REEMBOLSO COMPLETO",
      live: "EN VIVO EN EL CORREDOR",
    },
  };
  const c = C[lang];

  return (
    <div data-testid="landing-page">
      {/* Scarcity bar */}
      <div className="text-center py-2.5 font-mono text-xs" style={{ background: "var(--orange)", color: "white" }} data-testid="founding-scarcity-bar">
        <Lock size={11} className="inline mr-2 -mt-1" />
        {c.founding}
        <Link to="/founding" className="underline ml-3 font-semibold" data-testid="founding-bar-cta">{c.foundingCta} →</Link>
      </div>

      {/* HERO — 2-column on desktop, single stack on mobile (rebuilt 2026-06-15) */}
      <section className="relative overflow-hidden cd-dark-hero" data-testid="dark-hero">
        {/* Glow radial behind text — orange to simulate corridor city lights */}
        <div className="cd-dark-hero__glow" />
        {/* Subtle technical grid — gives the infra/maquiladora feel */}
        <div className="cd-dark-hero__grid" />
        <div className="relative container-cd py-20 md:py-28" style={{ color: "white" }}>
          <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-center">
            {/* LEFT — copy + CTA */}
            <div className="md:col-span-7" data-testid="hero-left-col">
              <h1
                className="font-display leading-[0.95] tracking-tighter font-extrabold"
                style={{
                  fontSize: "clamp(34px, 5.4vw, 72px)",
                  backgroundImage:
                    "linear-gradient(180deg, #ffffff 0%, #ffd5b0 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
                data-testid="hero-title"
              >
                {lang === "es"
                  ? "Tu planta ya está en el directorio. Solo falta reclamarla."
                  : "Your plant is already in the directory. All that's missing is your claim."}
              </h1>
              <p
                className="mt-7 font-body text-lg md:text-xl leading-relaxed max-w-xl"
                style={{ color: "rgba(255,255,255,0.78)" }}
                data-testid="hero-subtitle"
              >
                {lang === "es"
                  ? `${(stats?.manufacturers_listed ?? 644).toLocaleString()} ubicaciones de planta IMMEX listadas por gobierno. Los compradores de EE.UU. buscan en ambos idiomas.`
                  : `${(stats?.manufacturers_listed ?? 644).toLocaleString()} government-listed IMMEX plant locations. US buyers search in both languages.`}
              </p>
              {/* Founding-price callout */}
              <div
                className="mt-6 font-mono"
                style={{ fontSize: 15, letterSpacing: "0.02em" }}
                data-testid="hero-founding-price-callout"
              >
                <span
                  className="uppercase mr-2"
                  style={{
                    color: "rgba(255,255,255,0.7)",
                    fontSize: 12,
                    letterSpacing: "0.08em",
                  }}
                >
                  {lang === "es" ? "Precio Fundador:" : "Founding Price:"}
                </span>
                <span
                  style={{
                    color: "#666",
                    textDecoration: "line-through",
                    textDecorationThickness: "2px",
                    textDecorationColor: "rgba(102,102,102,0.7)",
                    marginRight: 8,
                  }}
                  data-testid="hero-founding-old"
                >
                  $299/mo
                </span>
                <span
                  style={{ color: "var(--orange)", fontWeight: 700, fontSize: 16 }}
                  data-testid="hero-founding-new"
                >
                  $149/mo
                </span>
                <span
                  className="uppercase ml-3"
                  style={{
                    color: "rgba(255,255,255,0.9)",
                    fontSize: 11,
                    letterSpacing: "0.08em",
                  }}
                >
                  {lang === "es" ? "— fijo de por vida" : "— locked for life"}
                </span>
              </div>
              {/* Primary CTA — full orange, dominant */}
              <div className="mt-8">
                <Link
                  to="/directory"
                  className="inline-flex items-center gap-2 cd-cta-glow"
                  style={{
                    background: "#FF6B00",
                    color: "white",
                    fontFamily: "var(--font-mono)",
                    fontSize: 14,
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    padding: "18px 36px",
                    borderRadius: 2,
                    border: "1px solid #FF6B00",
                  }}
                  data-testid="hero-cta-primary"
                >
                  {lang === "es" ? "Reclamar mi planta" : "Claim my plant"}
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>

            {/* RIGHT — mock plant profile card */}
            <div className="md:col-span-5" data-testid="hero-right-col">
              <div className="hero-mock-card" data-testid="hero-mock-card">
                <div className="hero-mock-card__overline">
                  CORRIDOR · MFG · {lang === "es" ? "EJEMPLO DE PERFIL" : "PROFILE PREVIEW"}
                </div>
                <div className="hero-mock-card__name" data-testid="mock-company-name">
                  TechMex Industrial S.A. de C.V.
                </div>
                <div className="hero-mock-card__location">
                  <MapPin size={11} className="inline -mt-0.5 mr-1" />
                  Ciudad Juárez · Chihuahua · MX
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-2">
                  <VerifiedBadge size="sm" testid="mock-verified-badge" />
                </div>

                <div className="mt-6">
                  <div className="hero-mock-card__label">
                    {lang === "es" ? "CAPACIDADES" : "CAPABILITIES"}
                  </div>
                  <div className="hero-mock-card__tags" data-testid="mock-capability-tags">
                    <span className="hero-mock-card__tag">CNC</span>
                    <span className="hero-mock-card__tag">IMMEX</span>
                    <span className="hero-mock-card__tag">ISO 9001</span>
                  </div>
                </div>

                <div className="mt-6">
                  <div
                    className="hero-mock-card__label"
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}
                  >
                    <span>{lang === "es" ? "CUMPLIMIENTO" : "COMPLIANCE SCORE"}</span>
                  </div>
                  <div
                    className="hero-mock-card__bar"
                    style={{ position: "relative", marginTop: 24 }}
                    data-testid="mock-compliance-bar"
                  >
                    <div className="hero-mock-card__bar-value">94%</div>
                    <div
                      className="hero-mock-card__bar-fill"
                      style={{ width: "94%" }}
                    />
                  </div>
                </div>

                <div className="hero-mock-card__footer" data-testid="mock-views-line">
                  <span className="hero-mock-card__live-dot" />
                  <Eye size={12} />
                  <span>
                    {lang === "es" ? "Compradores de EE.UU. vieron este perfil " : "US buyers viewed this profile "}
                    <span className="hero-mock-card__views-num">12</span>
                    {lang === "es" ? " veces esta semana" : " times this week"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BRIEF SEO V2 · #6 — COMPLIANCE-VAULT STATS (premium aspirational KPIs) */}
      <section
        style={{ background: "var(--ink)", color: "white", borderBottom: "1px solid rgba(255,255,255,0.08)" }}
        data-testid="compliance-stats-strip"
      >
        <div
          className="container-cd grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-0 text-center md:text-left"
          style={{ paddingTop: 80, paddingBottom: 80 }}
        >
          <ComplianceStat
            value={(stats?.manufacturers_listed ?? 644).toLocaleString("en-US")}
            label={lang === "es" ? "Ubicaciones de planta listadas por gobierno" : "Government-listed plant locations"}
            testid="cs-plants"
          />
          <ComplianceStat
            value={String(catCount ?? 81)}
            label={lang === "es" ? "Categorías de capacidad" : "Capability categories"}
            border
            testid="cs-categories"
          />
          <ComplianceStat
            value={String(stats?.total_documents_secured ?? "—")}
            label={lang === "es" ? "Documentos de cumplimiento resguardados" : "Compliance documents secured"}
            border
            testid="cs-documents"
          />
          <ComplianceStat
            value="ES · EN"
            label={lang === "es" ? "Bilingüe por diseño" : "Bilingual by design"}
            border
            testid="cs-bilingual"
          />
        </div>
      </section>

      {/* MINI TESTIMONIALS STRIP — removed 2026-09 (V1 refresh): quotes were
          illustrative, not from real customers. Reinstate only with real,
          attributable testimonials. */}

      {/* BRIEF SEO V2 · #7 — COMPLIANCE VAULT TRUST BADGES */}
      <section
        style={{ background: "white", borderBottom: "1px solid var(--line)" }}
        data-testid="compliance-vault-badges"
      >
        <div className="container-cd py-20 md:py-24">
          <div className="overline text-center mb-6" data-testid="vault-kicker">
            {lang === "es" ? "COMPLIANCE VAULT · CERTIFICACIONES QUE RASTREAMOS" : "COMPLIANCE VAULT · CERTIFICATIONS WE TRACK"}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 items-center justify-items-center">
            <CertBadge label="ISO 9001" testid="cert-iso9001" />
            <CertBadge label="IATF 16949" testid="cert-iatf16949" />
            <CertBadge label="IMMEX Certified" testid="cert-immex" />
            <CertBadge label="USMCA Compliant" testid="cert-usmca" />
          </div>
        </div>
      </section>

      {/* BRIEF A · LIVE SOCIAL PROOF STRIP — HIDDEN per 2026-06-15 directive.
          Replaced with a single horizontal trust strip below. */}
      {/* <SocialProofStrip stats={toolkitStats} t={t} /> */}
      <section
        style={{
          background: "var(--ink)",
          borderTop: "1px solid rgba(255,107,0,0.3)",
          borderBottom: "1px solid rgba(255,107,0,0.3)",
        }}
        data-testid="trust-strip-banner"
      >
        <div
          className="container-cd text-center font-mono"
          style={{
            paddingTop: 18,
            paddingBottom: 18,
            color: "rgba(255,255,255,0.78)",
            fontSize: 11,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            lineHeight: 1.6,
          }}
        >
          {lang === "es"
            ? "PROVEEDORES IMMEX LISTADOS POR GOBIERNO · CORREDOR CHIHUAHUA–EL PASO (JUÁREZ · PARRAL · CHIHUAHUA CITY) · ZONA DE COMERCIO USMCA · DESDE 2025"
            : "GOVERNMENT-LISTED IMMEX SUPPLIERS · CHIHUAHUA–EL PASO CORRIDOR (JUÁREZ · PARRAL · CHIHUAHUA CITY) · USMCA TRADE ZONE · EST. 2025"}
        </div>
      </section>

      {/* BRIEF A · TRUST SIGNALS — 4 badges */}
      <section
        style={{ background: "white", borderBottom: "1px solid var(--line)" }}
        data-testid="trust-signals-section"
      >
        <div className="container-cd py-20 md:py-24 grid grid-cols-2 md:grid-cols-4 gap-6">
          <TrustBadge icon={ShieldCheck} label={t("ba.ts.immex")} testid="ts-immex" />
          <TrustBadge icon={MapPin} label={t("ba.ts.corridor")} testid="ts-corridor" />
          <TrustBadge icon={Flag} label={t("ba.ts.usmca")} testid="ts-usmca" />
          <TrustBadge icon={Star} label={t("ba.ts.first")} testid="ts-first" />
        </div>
      </section>

      {/* BRIEF A · WHY YOU CAN'T FIND THIS ANYWHERE ELSE */}
      <section
        className="section-cd"
        style={{ background: "var(--surface)", borderBottom: "1px solid var(--line)" }}
        data-testid="why-section"
      >
        <div className="container-cd">
          <div className="overline text-center">{t("ba.why.kicker")}</div>
          <div className="mt-10 grid md:grid-cols-3 gap-0" style={{ border: "1px solid var(--line)" }}>
            <WhyCol icon={ShieldCheck} h={t("ba.why.c1.h")} b={t("ba.why.c1.b")} testid="why-immex" />
            <WhyCol icon={Globe2} h={t("ba.why.c2.h")} b={t("ba.why.c2.b")} testid="why-bilingual" border />
            <WhyCol icon={Zap} h={t("ba.why.c3.h")} b={t("ba.why.c3.b")} testid="why-rfq" border />
          </div>
        </div>
      </section>

      {/* FOUR PILLARS — the organizing system: Find Suppliers · Verify Evidence ·
          Claim or Confirm a Plant · Request Evidence */}
      <section style={{ background: "var(--ink)", color: "white", borderBottom: "1px solid var(--line)" }} data-testid="four-pillars-section">
        <div className="container-cd py-20 md:py-24">
          <div className="overline" style={{ color: "var(--orange)" }}>
            {lang === "es" ? "CÓMO FUNCIONA CORRIDOR DIGITAL" : "HOW CORRIDOR DIGITAL WORKS"}
          </div>
          <h2 className="font-display text-3xl md:text-5xl mt-6 leading-[1] max-w-3xl">
            {lang === "es"
              ? "Cuatro maneras de usar el directorio de registro del corredor."
              : "Four ways to use the corridor's directory of record."}
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0" style={{ border: "1px solid rgba(255,255,255,0.15)" }}>
            {[
              {
                n: "01", to: "/plants", guide: "/guides/find-suppliers",
                h: lang === "es" ? "Encuentra proveedores" : "Find Suppliers",
                b: lang === "es"
                  ? "Ubicaciones de planta IMMEX listadas por gobierno, con búsqueda por categorías de capacidad en español e inglés."
                  : "Government-listed IMMEX plant locations, searchable across capability categories in English and Spanish.",
              },
              {
                n: "02", to: "/digest", guide: "/guides/verify-evidence",
                h: lang === "es" ? "Verifica evidencia" : "Verify Evidence",
                b: lang === "es"
                  ? "Cuatro etiquetas de confianza que nunca se mezclan. Las capacidades respaldadas por evidencia citan URL de fuente y texto exacto."
                  : "Four trust labels, never conflated. Evidence-backed capabilities cite a source URL and the exact supporting text.",
              },
              {
                n: "03", to: "/guides/claim-your-plant", guide: null,
                h: lang === "es" ? "Reclama o confirma tu planta" : "Claim or Confirm a Plant",
                b: lang === "es"
                  ? "El personal de la planta confirma su ubicación exacta, propone capacidades y adjunta documentación privada — con confirmación por correo y revisión manual."
                  : "Plant contacts confirm their exact location, propose capabilities, and attach private documentation — email-confirmed and reviewed by hand.",
              },
              {
                n: "04", to: "/guides/request-evidence", guide: null,
                h: lang === "es" ? "Solicita evidencia" : "Request Evidence",
                b: lang === "es"
                  ? "Los compradores solicitan acceso a documentos de cumplimiento en la Bóveda. Cada acceso es revisado, delimitado y auditado."
                  : "Buyers request access to compliance documents held in the Vault. Every grant is reviewed, scoped, and audited.",
              },
            ].map((p, i) => (
              <Link
                key={p.n}
                to={p.to}
                className="block p-7 group"
                style={{
                  borderRight: i < 3 ? "1px solid rgba(255,255,255,0.15)" : "none",
                  borderBottom: "none",
                  transition: "background-color 0.2s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(255,107,0,0.08)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                data-testid={`pillar-card-${i}`}
              >
                <div className="font-mono" style={{ color: "var(--orange)", fontSize: 13, letterSpacing: "0.12em", fontWeight: 700 }}>
                  {p.n}
                </div>
                <h3 className="font-heading text-lg mt-3 font-semibold" style={{ color: "white" }}>{p.h}</h3>
                <p className="mt-2 text-sm leading-snug" style={{ color: "rgba(255,255,255,0.65)" }}>{p.b}</p>
                <div className="mt-4 inline-flex items-center gap-1.5 font-mono text-xs" style={{ color: "var(--orange)", letterSpacing: "0.06em" }}>
                  {lang === "es" ? "Abrir" : "Open"} <ArrowRight size={12} />
                </div>
                {p.guide ? (
                  <div className="mt-1">
                    <span
                      className="font-mono"
                      style={{ color: "rgba(255,255,255,0.45)", fontSize: 10, letterSpacing: "0.04em" }}
                    >
                      {lang === "es" ? "Guía: " : "Guide: "}
                      <span
                        role="link"
                        tabIndex={0}
                        className="underline cursor-pointer"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.assign(p.guide); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); window.location.assign(p.guide); } }}
                        data-testid={`pillar-guide-link-${i}`}
                      >
                        {lang === "es" ? "cómo funciona" : "how it works"}
                      </span>
                    </span>
                  </div>
                ) : null}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF MARQUEE — removed 2026-09 (V1 refresh): names were
          illustrative placeholders, not real customers. */}

      {/* PROBLEM */}
      <section className="section-cd" style={{ background: "var(--ink)", color: "white" }} data-testid="problem-section">
        <div className="container-cd text-center">
          <div className="overline" style={{ color: "rgba(255,255,255,0.6)" }}>
            {t("landing.problem.kicker")}
          </div>
          <h2
            className="font-display text-4xl md:text-5xl mt-4 leading-[0.95] max-w-3xl mx-auto"
            data-testid="problem-headline"
          >
            {lang === "es"
              ? "Los compradores de EE.UU. no te encuentran. Nosotros lo arreglamos."
              : "US buyers can't find you. We fix that."}
          </h2>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section style={{ background: "var(--surface)", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)" }} className="section-cd">
        <div className="container-cd">
          <div className="overline">{t("landing.what.kicker")}</div>
          <h2 className="font-display text-4xl md:text-6xl mt-4 leading-[0.95] max-w-3xl">
            {t("landing.what.title")}
          </h2>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10" data-testid="three-tools-row">
            {[
              {
                num: "01",
                title: t("landing.what.f1.t"),
                line: lang === "es"
                  ? "Ficha técnica bilingüe. URL para compartir."
                  : "Bilingual spec-sheet profile. Shareable URL.",
              },
              {
                num: "02",
                title: t("landing.what.f2.t"),
                line: lang === "es"
                  ? "Sube IMMEX, USMCA, ISO. Enlaces con caducidad."
                  : "Upload IMMEX, USMCA, ISO. Time-boxed share links.",
              },
              {
                num: "03",
                title: t("landing.what.f3.t"),
                line: lang === "es"
                  ? "Los RFQs llegan a tu bandeja. Responde en cualquier idioma."
                  : "RFQs land in your inbox. Reply in either language.",
              },
            ].map(({ num, title, line }, i) => (
              <div
                key={i}
                className="text-center md:text-left"
                data-testid={`three-tools-card-${i}`}
              >
                <div
                  className="font-mono"
                  style={{
                    color: "var(--orange)",
                    fontSize: 14,
                    letterSpacing: "0.12em",
                    fontWeight: 700,
                  }}
                  data-testid={`three-tools-num-${i}`}
                >
                  {num}
                </div>
                <h3 className="font-heading text-xl mt-3 font-semibold">{title}</h3>
                <p className="mt-2 text-sm leading-snug" style={{ color: "var(--ink-2)" }}>{line}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CROSS-BORDER DATA BRIDGE — the moat */}
      <section
        className="section-cd relative"
        style={{ background: "var(--ink)", color: "white" }}
      >
        {/* ENTERPRISE badge — top-right of section header */}
        <div
          className="absolute"
          style={{
            top: 20,
            right: 20,
            background: "var(--orange)",
            color: "#0a0c12",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            padding: "5px 12px",
            borderRadius: 999,
            zIndex: 5,
          }}
          data-testid="bridge-enterprise-badge"
        >
          {lang === "es" ? "EMPRESARIAL" : "ENTERPRISE"}
        </div>
        <div className="container-cd grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <div className="overline" style={{ color: "var(--orange)" }}>{c.bridgeKicker}</div>
            <h2 className="font-display text-5xl md:text-7xl mt-4 leading-[0.92]">{c.bridgeTitle}</h2>
            <p className="mt-8 text-lg" style={{ color: "rgba(255,255,255,0.75)", maxWidth: 620 }}>{c.bridgeBody}</p>
            <ul className="mt-8 space-y-2 font-mono text-xs">
              {c.bridgeBullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Zap size={12} style={{ color: "var(--orange)", marginTop: 4, flexShrink: 0 }} /> {b}
                </li>
              ))}
            </ul>
            <Link to="/pricing" className="btn btn-primary btn-lg mt-10 inline-flex" data-testid="bridge-cta">
              {c.bridgeCta} <ArrowRight size={16} />
            </Link>
          </div>
          {/* Bridge mockup */}
          <div className="md:col-span-5" data-testid="bridge-mock">
            <div className="cd-card p-6" style={{ background: "white", color: "var(--ink)" }}>
              <div className="overline">CROSS-BORDER DATA BRIDGE · {lang === "es" ? "VISTA DE EJEMPLO" : "DEMO PREVIEW"}</div>
              <div className="mt-4 flex items-baseline justify-between">
                <div>
                  <div className="font-display text-2xl">INDUSTRIAS FRONTERA MX</div>
                  <div className="font-mono text-xs mt-1" style={{ color: "var(--ink-2)" }}>FOR: Detroit Auto Group · Procurement</div>
                </div>
                <div className="cd-chip cd-chip-green">✓ VERIFIED</div>
              </div>
              <div className="mt-6 font-display text-7xl" style={{ color: "var(--green)" }}>94%</div>
              <div className="overline">COMPLIANCE SCORE</div>
              <div className="mt-2 h-3" style={{ background: "var(--bg)", border: "1px solid var(--line)" }}>
                <div style={{ width: "94%", height: "100%", background: "var(--green)" }} />
              </div>
              <div className="mt-6 space-y-2 font-mono text-xs">
                <Row label="IMMEX Permit" status="VALID" days="487d left" color="var(--green)" />
                <Row label="USMCA Certificate" status="VALID" days="312d left" color="var(--green)" />
                <Row label="ISO 9001:2015" status="EXPIRING" days="42d left" color="var(--yellow)" />
                <Row label="ISO 14001:2015" status="VALID" days="201d left" color="var(--green)" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CASE STUDY — removed 2026-09 (V1 refresh): the case referenced the
          demo record. Reinstate only with a real, attributable customer case. */}

      {/* BUYERS SECTION */}
      <section className="section-cd" style={{ background: "var(--surface)", borderTop: "1px solid var(--line)" }}>
        <div className="container-cd grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6 cd-card" style={{ backgroundImage: `url('${LOGISTICS_IMG}')`, backgroundSize: "cover", backgroundPosition: "center", minHeight: 420 }} />
          <div className="md:col-span-6">
            <div className="overline">{t("landing.buyers.kicker")}</div>
            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-[0.95]">{t("landing.buyers.title")}</h2>
            <p className="mt-6 text-lg leading-relaxed" style={{ color: "var(--ink-2)" }}>{t("landing.buyers.body")}</p>
            <div className="mt-8">
              <Link to="/directory" className="btn btn-dark btn-lg" data-testid="buyers-cta">
                {t("landing.buyers.cta")} <ArrowRight size={16} />
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 font-mono text-xs">
              <Cap icon={Factory} label="CNC" />
              <Cap icon={Factory} label="INJECTION" />
              <Cap icon={Factory} label="HARNESSES" />
              <Cap icon={Factory} label="SHEET METAL" />
              <Cap icon={Factory} label="ASSEMBLY" />
              <Cap icon={Truck} label="LOGISTICS" />
            </div>
          </div>
        </div>
      </section>

      {/* GUARANTEE — standalone centered section with orange border, above founder */}
      <section
        className="py-20"
        style={{ background: "white", borderTop: "1px solid var(--line)" }}
        data-testid="guarantee-section"
      >
        <div className="container-cd flex justify-center">
          <div
            className="text-center max-w-2xl w-full"
            style={{
              border: "1px solid #FF6B00",
              padding: 16,
              background: "white",
            }}
            data-testid="guarantee-card"
          >
            <div
              className="font-heading font-bold uppercase"
              style={{
                color: "var(--ink)",
                fontSize: 14,
                letterSpacing: "0.12em",
              }}
              data-testid="guarantee-headline"
            >
              <ShieldCheck size={14} className="inline -mt-1 mr-2" style={{ color: "#FF6B00" }} />
              {c.guarantee}
            </div>
            <p
              className="mt-3 font-mono text-xs"
              style={{ color: "var(--ink-2)", letterSpacing: "0.04em" }}
              data-testid="guarantee-subtext"
            >
              {lang === "es"
                ? "Sin consulta de comprador verificado de EE.UU. en 60 días — reembolso completo. Sin preguntas."
                : "No verified US buyer inquiry in 60 days — full refund. No questions asked."}
            </p>
          </div>
        </div>
      </section>

      {/* FOUNDER + LOCATION TRUST */}
      <section className="section-cd">
        <div className="container-cd grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <div className="overline">FOUNDER · LAS CRUCES, NM</div>
            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-[0.95]">
              {lang === "es" ? "Construido a 45 minutos de tu planta." : "Built 45 minutes from your plant."}
            </h2>
            <p className="mt-6 text-base" style={{ color: "var(--ink-2)" }}>
              {lang === "es"
                ? "Lo construye alguien del corredor: Derick Varela, Las Cruces NM. Visita Juárez cada semana y habla ambos idiomas."
                : "Built by someone in the corridor — Derick Varela, Las Cruces NM. Visits Juárez weekly. Bilingual."}
            </p>
          </div>
          <div className="md:col-span-7 grid grid-cols-2 gap-0" style={{ border: "1px solid var(--line)" }}>
            <Trust icon={MapPin} v="45 min" l={lang === "es" ? "de Las Cruces a Juárez" : "Las Cruces to Juárez"} />
            <Trust icon={Clock} v={lang === "es" ? "Semanal" : "Weekly"} l={lang === "es" ? "visitas a Juárez" : "Juárez visits"} last />
            <Trust icon={TrendingUp} v={(stats?.manufacturers_listed ?? 644).toLocaleString()} l={lang === "es" ? "ubicaciones de planta listadas" : "plant locations listed"} />
            <Trust icon={Globe2} v="ES · EN" l={lang === "es" ? "bilingüe por diseño" : "bilingual by design"} last bottom />
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section-cd" style={{ background: "var(--bg)" }}>
        <div className="container-cd text-center">
          <div className="overline">{c.finalKicker}</div>
          <h2 className="font-display text-5xl md:text-7xl mt-6 leading-[0.9] max-w-4xl mx-auto whitespace-pre-line">
            {c.finalTitle.split("\n")[0]}
            <br />
            <span style={{ color: "var(--orange)" }}>{c.finalTitle.split("\n")[1]}</span>
          </h2>
          <div className="mt-10 flex justify-center gap-4 flex-wrap">
            <Link to="/register" className="btn btn-primary btn-lg" data-testid="footer-cta-signup">
              {c.finalCta} <ArrowRight size={16} />
            </Link>
            <Link to="/founding" className="btn btn-dark btn-lg" data-testid="footer-cta-founding">
              <Lock size={14} /> {c.foundingCta}
            </Link>
            <Link to="/directory" className="btn btn-ghost btn-lg" data-testid="footer-cta-directory">
              {c.finalCta2}
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL URGENCY — full-width, large Bebas Neue + circle progress + CTA */}
      <section
        className="cd-urgency-section"
        style={{
          background: "#0a0c12",
          borderTop: "1px solid rgba(255,107,0,0.32)",
          position: "relative",
          overflow: "hidden",
        }}
        data-testid="urgency-section"
      >
        {/* Subtle orange radial glow */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(circle at 50% 0%, rgba(255,107,0,0.18) 0%, transparent 55%)",
            pointerEvents: "none",
          }}
        />
        <div className="relative container-cd py-20 md:py-24 text-center">
          <h2
            className="cd-urgency-headline mx-auto"
            style={{
              fontFamily: "'Bebas Neue', 'Archivo Black', sans-serif",
              fontSize: "clamp(48px, 8vw, 112px)",
              fontWeight: 400,
              color: "white",
              letterSpacing: "0.04em",
              lineHeight: 0.95,
              maxWidth: 1100,
            }}
            data-testid="urgency-headline"
          >
            {lang === "es"
              ? `QUEDAN ${spotsLeft} LUGARES FUNDADORES`
              : `${spotsLeft} FOUNDING SPOTS REMAIN`}
          </h2>

          {/* 10 circles — filled = taken, empty = remaining */}
          <div
            className="mt-10 flex items-center justify-center gap-3 flex-wrap"
            data-testid="urgency-circles"
          >
            {Array.from({ length: 10 }).map((_, i) => {
              const filled = i < spotsTaken;
              return (
                <span
                  key={i}
                  aria-hidden="true"
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: filled ? "#FF6B00" : "transparent",
                    border: filled
                      ? "1px solid #FF6B00"
                      : "1px solid rgba(255,107,0,0.45)",
                    boxShadow: filled ? "0 0 12px rgba(255,107,0,0.6)" : "none",
                    transition: "all 0.3s ease",
                  }}
                  data-testid={`urgency-circle-${filled ? "filled" : "empty"}-${i}`}
                />
              );
            })}
          </div>

          <p
            className="mt-8 font-mono"
            style={{
              color: "rgba(255,255,255,0.82)",
              fontSize: 13,
              letterSpacing: "0.04em",
            }}
            data-testid="urgency-subline"
          >
            {lang === "es"
              ? "Precio sube a $299/mo cuando se llenen los 10 lugares."
              : "Price rises to $299/mo once all 10 spots are filled."}
          </p>

          <div className="mt-10">
            <Link
              to="/founding"
              className="inline-flex items-center gap-2 cd-cta-glow"
              style={{
                background: "#FF6B00",
                color: "white",
                fontFamily: "var(--font-mono)",
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                padding: "18px 38px",
                borderRadius: 2,
                border: "1px solid #FF6B00",
              }}
              data-testid="urgency-cta"
            >
              {lang === "es" ? "Asegurar mi lugar" : "Secure my spot"}
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

/* Mini testimonial card — used in the testimonials strip below the stats */
function MiniTestimonial({ quote, company, testid }) {
  return (
    <figure
      className="cd-mini-testimonial"
      style={{
        background: "#0b0d12",
        borderLeft: "2px solid #FF6B00",
        padding: 12,
        color: "white",
      }}
      data-testid={testid}
    >
      <blockquote
        className="font-body italic"
        style={{
          fontSize: 14,
          lineHeight: 1.55,
          color: "rgba(255,255,255,0.88)",
          margin: 0,
        }}
      >
        “{quote}”
      </blockquote>
      <figcaption
        className="font-mono uppercase mt-3"
        style={{
          fontSize: 11,
          letterSpacing: "0.08em",
          color: "rgba(255,255,255,0.55)",
        }}
      >
        {company}
      </figcaption>
    </figure>
  );
}

function Cap({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2" style={{ border: "1px solid var(--line)", background: "white" }}>
      <Icon size={14} />
      <span>{label}</span>
    </div>
  );
}

function Trust({ icon: Icon, v, l, last, bottom }) {
  return (
    <div className="p-6 bg-white" style={{ borderRight: last ? "none" : "1px solid var(--line)", borderBottom: bottom ? "none" : "1px solid var(--line)" }}>
      <Icon size={20} strokeWidth={1.5} />
      <div className="font-display text-3xl mt-3">{v}</div>
      <div className="overline mt-1">{l}</div>
    </div>
  );
}

function Row({ label, status, days, color }) {
  return (
    <div className="flex items-center justify-between py-2" style={{ borderBottom: "1px solid var(--line-soft)" }}>
      <div className="flex items-center gap-2">
        <span className="dot" style={{ background: color }} />
        <span style={{ color: "var(--ink)" }}>{label}</span>
      </div>
      <div className="flex items-center gap-3">
        <span style={{ color: color }}>{status}</span>
        <span style={{ color: "var(--ink-2)" }}>{days}</span>
      </div>
    </div>
  );
}

/* Brief SEO V2 · #6 — Compliance Vault stat (hardcoded aspirational KPI) */
function ComplianceStat({ value, label, border, testid, live }) {
  return (
    <div
      className="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-4 justify-center md:justify-start"
      style={
        border
          ? { borderLeft: "1px solid rgba(255,255,255,0.15)", paddingLeft: 32, paddingRight: 24 }
          : { paddingRight: 24 }
      }
      data-testid={testid}
    >
      <div
        className="font-display tracking-tight font-extrabold leading-none inline-flex items-center gap-2"
        style={{
          fontSize: "clamp(36px, 4.5vw, 56px)",
          backgroundImage:
            "linear-gradient(135deg, #FFB37A 0%, #FF6B00 50%, #ffd5b0 100%)",
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          color: "transparent",
          filter: "drop-shadow(0 0 18px rgba(255,107,0,0.25))",
        }}
      >
        {value}
        {live ? (
          <span
            aria-hidden="true"
            style={{
              display: "inline-block",
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: "#86efac",
              boxShadow: "0 0 10px rgba(134,239,172,0.9)",
              animation: "cd-ai-dot-pulse 1.8s ease-out infinite",
              filter: "none",
              backgroundClip: "border-box",
              WebkitBackgroundClip: "border-box",
            }}
          />
        ) : null}
      </div>
      <div
        className="font-mono uppercase tracking-wider leading-snug"
        style={{
          color: "rgba(255,255,255,0.72)",
          fontSize: 14,  /* +2px from previous text-xs (12px) */
        }}
      >
        {label}
      </div>
    </div>
  );
}

/* Brief SEO V2 · #7 — Compliance Vault cert badge (monochrome → full color on hover) */
function CertBadge({ label, testid }) {
  return (
    <div
      className="cd-cert-badge flex items-center gap-3"
      data-testid={testid}
    >
      <Award size={22} strokeWidth={1.5} className="cd-cert-badge__icon" />
      <span className="font-mono text-sm tracking-tight font-semibold cd-cert-badge__label">
        {label}
      </span>
    </div>
  );
}

/* Brief A — animated count-up social-proof strip directly under the hero. */
function SocialProofStrip({ stats, t }) {
  const plants = stats?.plants_in_directory ?? 0;
  const rfqsWeek = stats?.rfqs_last_7d ?? 0;
  const signupsMonth = stats?.field_signups_this_month ?? 0;
  const hasAny = plants + rfqsWeek + signupsMonth > 0;
  return (
    <section
      style={{ background: "var(--ink)", color: "white", borderBottom: "1px solid rgba(255,255,255,0.08)" }}
      data-testid="social-proof-strip"
    >
      <div className="container-cd py-5 md:py-6">
        {hasAny ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-12 text-center md:text-left">
            <SPItem n={plants} label={t("ba.sp.plants")} testid="sp-plants" />
            <SPItem n={rfqsWeek} label={t("ba.sp.rfqs_week")} testid="sp-rfqs" border />
            <SPItem n={signupsMonth} label={t("ba.sp.signups_month")} testid="sp-signups" border />
          </div>
        ) : (
          <div className="text-center font-mono text-sm" style={{ color: "rgba(255,255,255,0.7)" }} data-testid="sp-fallback">
            {t("ba.sp.fallback")}
          </div>
        )}
      </div>
    </section>
  );
}

function SPItem({ n, label, testid, border }) {
  const display = useCountUp(n);
  return (
    <div
      className="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-3 justify-center md:justify-start"
      style={border ? { borderLeft: "1px solid rgba(255,255,255,0.1)", paddingLeft: 24 } : {}}
      data-testid={testid}
    >
      <div className="font-display text-4xl md:text-5xl leading-none" style={{ color: "var(--orange)" }}>
        {display}
      </div>
      <div className="font-mono text-[10px] md:text-xs uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.75)" }}>
        {label}
      </div>
    </div>
  );
}

/* Cheap count-up — RAF-driven so it ramps from 0 → target on mount/change. */
function useCountUp(target, duration = 900) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!target || target <= 0) { setVal(0); return; }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      setVal(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

function TrustBadge({ icon: Icon, label, testid }) {
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <div
        className="flex-shrink-0 w-10 h-10 flex items-center justify-center"
        style={{ background: "var(--ink)", color: "white", borderRadius: 2 }}
      >
        <Icon size={18} strokeWidth={1.6} />
      </div>
      <div className="font-heading text-sm leading-tight">{label}</div>
    </div>
  );
}

function WhyCol({ icon: Icon, h, b, testid, border }) {
  return (
    <div
      className="p-8 md:p-10 bg-white"
      style={border ? { borderLeft: "1px solid var(--line)" } : {}}
      data-testid={testid}
    >
      <Icon size={28} strokeWidth={1.5} />
      <h3 className="font-heading text-xl md:text-2xl mt-6 font-semibold">{h}</h3>
      <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--ink-2)" }}>{b}</p>
    </div>
  );
}
